"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { Inter, Caveat } from "next/font/google";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { appDetails } from "../data";

// Reuse generic components from industries (excluding IndustryHero, IndustrySplitContent, and IndustryVideoStats to keep apps fully dynamic)
import IndustryTestimonial from "../../industries/components/IndustryTestimonial";
import IndustryServiceManagement from "../../industries/components/IndustryServiceManagement";
import IndustrySecureDocs from "../../industries/components/IndustrySecureDocs";
import IndustryFeaturesGrid from "../../industries/components/IndustryFeaturesGrid";
import IndustryAppGrid from "../../industries/components/IndustryAppGrid";
import IndustryUsersMosaic from "../../industries/components/IndustryUsersMosaic";
import IndustryCTA from "../../industries/components/IndustryCTA";
import OdooMoreCallout from "../../industries/components/OdooMoreCallout";

const inter = Inter({ subsets: ["latin"], display: "swap" });
const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

// Custom Centered AppHero matching the user's Odoo template exactly with design, colors, and fonts
const AppHero = ({ data }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState(`/odoo/icons/apps/${data.icon}.svg?v=2`);
  const appNameClean = data.title.replace("Odoo ", "");

  useEffect(() => {
    setImgSrc(`/odoo/icons/apps/${data.icon}.svg?v=2`);
  }, [data.icon]);

  return (
    <section className="relative pt-12 pb-16 px-6 sm:px-12 lg:px-24 text-center z-10 overflow-hidden">
      {/* Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#f1833b]/10 rounded-full blur-[140px] pointer-events-none -z-10" />

      <div className="container mx-auto max-w-4xl relative z-10">
        {/* Floating App Icon inside a beautiful glass block */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-20 h-20 bg-white rounded-3xl p-4 shadow-2xl border border-white/20 mx-auto mb-8 flex items-center justify-center backdrop-blur-md"
        >
          <Image
            src={imgSrc}
            width={80}
            height={80}
            className="w-full h-full object-contain"
            alt={data.title}
            onError={() => {
              setImgSrc("/odoo/icons/green_thumbs-up.svg");
            }}
          />
        </motion.div>

        {/* Cursive Signature Tagline - matching orange color theme */}
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className={`text-5xl sm:text-7xl md:text-8xl leading-tight mb-8 tracking-tight ${caveat.className}`}
        >
          <span className="text-white font-normal">{appNameClean}</span>{" "}
          <span className="text-[#f1833b] font-normal">{data.heroText}</span>
        </motion.h1>

        {/* Description - matching fonts & lead exactly */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <p className="text-lg sm:text-2xl text-gray-300 mb-12 leading-relaxed font-light max-w-3xl mx-auto font-sans">
            <strong className="text-white font-bold">Imagine one AI-native {appNameClean} platform</strong> for all your {appNameClean.toLowerCase()} operations, from tracking and coordination to complete real-time reporting. Fast, complete, and simple.
          </p>

          {/* Action Triggers with exact orange brand colors */}
          <div className="flex flex-wrap items-center justify-center gap-4 mb-8 relative">
            <Link href={`/odoo/start/${(data?.title || 'app').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')}`}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-3.5 bg-[#f1833b] hover:bg-[#e06b24] text-white font-bold rounded-xl transition-all shadow-lg text-base"
              >
                Start now - It's free
              </motion.button>
            </Link>

            {/* Meet an advisor Button */}
            <motion.a 
              href="https://cal.com/codeqlik/odoo" 
              target="_blank" 
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-3.5 bg-white/10 hover:bg-white/20 border border-white/20 text-gray-200 font-bold rounded-xl transition-all text-base flex items-center gap-2"
            >
              Meet an advisor
            </motion.a>
          </div>

          {/* Small Print disclaimer */}
          <small className="text-gray-400 block max-w-lg mx-auto text-sm leading-relaxed mb-12">
            Send and receive unlimited invoices and bills, for free, forever.{" "}
            <a href="#" className="underline text-gray-300 hover:text-[#f1833b] transition-colors">
              See why
            </a>
          </small>
        </motion.div>
      </div>

      {/* Interactive signature elements - bottom left and bottom right doodles */}
      <div className="absolute left-[8%] bottom-[10%] hidden lg:block opacity-60 pointer-events-none">
        <svg width="100" height="80" viewBox="0 0 100 80" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 50 C 30 70, 70 10, 90 30" stroke="#f1833b" strokeWidth="3" strokeLinecap="round" strokeDasharray="5,5" />
          <path d="M15 45 L5 50 L10 60" stroke="#f1833b" strokeWidth="3" strokeLinecap="round" />
        </svg>
      </div>

      {/* Dynamic Localized Badge for Accounting - Indian Flag & Cursive "GST compliant" in orange */}
      {(data.icon === "accountant" || data.icon === "accounting") && (
        <div className="absolute right-[8%] bottom-[10%] hidden lg:flex items-center gap-3 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 shadow-xl backdrop-blur-sm pointer-events-none">
          <span className="text-xl">🇮🇳</span>
          <div className="text-left">
            <span className={`text-xl text-[#f1833b] leading-none block ${caveat.className} font-bold`}>
              GST compliant
            </span>
          </div>
          <svg className="w-5 h-5 text-[#f1833b] ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>
      )}
    </section>
  );
};

// Custom dynamic SplitContent component that correctly loads dynamic values and screenshots/gifs
const AppSplitContent = ({ section }) => {
  return (
    <section className="py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-white/5 rounded-tl-[120px] rounded-br-[120px] border-y border-white/5 my-12">
      <div className="container mx-auto max-w-6xl">
        <h2 
          className="text-4xl md:text-6xl font-bold mb-16 leading-tight text-white text-center sm:text-left"
          dangerouslySetInnerHTML={{ __html: section.title }}
        />

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Main Left Content & Image Grid */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7 space-y-8"
          >
            <p 
              className="text-xl text-gray-300 leading-relaxed font-light"
              dangerouslySetInnerHTML={{ __html: section.items[0].text }}
            />

            <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 relative z-10 bg-neutral-900/50 aspect-video flex items-center justify-center">
              <Image 
                src={section.items[0].image} 
                fill
                sizes="(max-width: 768px) 100vw, 50vw"
                className="w-full h-full object-cover rounded-2xl hover:scale-102 transition-transform duration-300" 
                alt="" 
              />
            </div>
          </motion.div>

          {/* Secondary Right Content & Image Grid */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 lg:mt-24 space-y-8"
          >
            {section.items[1] && (
              <>
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-neutral-900/50 aspect-video flex items-center justify-center relative">
                  <Image 
                    src={section.items[1].image} 
                    fill
                    sizes="(max-width: 768px) 100vw, 50vw"
                    className="w-full h-full object-cover rounded-2xl hover:scale-102 transition-transform duration-300" 
                    alt="" 
                  />
                </div>
                <p 
                  className="text-lg text-gray-300 leading-relaxed font-light"
                  dangerouslySetInnerHTML={{ __html: section.items[1].text }}
                />
              </>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Custom dynamic VideoStats component to replace the hardcoded industry video component
const AppVideoStats = ({ section, appName }) => {
  return (
    <section className="py-24 px-6 sm:px-12 lg:px-24 bg-white/5 relative overflow-hidden my-12 border-y border-white/5 rounded-[60px]">
      <div className="container mx-auto max-w-6xl">
        {/* Header with Dynamic Stats */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-16 gap-8">
          <motion.h2 
            className="text-4xl md:text-6xl font-bold text-white leading-tight"
          >
            Next-level<br /> 
            <span className="text-[#f1833b]">{appName}</span>
          </motion.h2>
          
          {section.stats && (
            <motion.div 
              className="flex items-center gap-6 bg-white/5 p-8 rounded-3xl border border-white/10 shadow-sm backdrop-blur-sm"
            >
              <div className="text-right">
                <h3 className="text-4xl font-bold text-white">{section.stats}</h3>
              </div>
            </motion.div>
          )}
        </div>

        {/* Content with Dynamic Video */}
        <div className="grid lg:grid-cols-12 gap-16 items-center">
          <motion.div 
            className="lg:col-span-5 space-y-6 text-xl text-gray-300 leading-relaxed font-light"
          >
            <p className="text-2xl text-white font-normal mb-4">
              {section.title}
            </p>
            <p>
              {section.subtext}
            </p>
          </motion.div>

          <motion.div 
            className="lg:col-span-7 rounded-[2rem] overflow-hidden border-[8px] border-white/5 shadow-2xl relative group"
          >
            <video autoPlay loop muted playsInline className="w-full">
              <source src={section.video} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-slate-900/10 group-hover:bg-transparent transition-all" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function AppPage({ params }) {
  const { id } = React.use(params);

  // Fetch app details or fallback to CRM if not found
  const data = appDetails[id] || appDetails["crm"];
  const appNameClean = data.title.replace("Odoo ", "");

  // Custom fallback icon path if heroIcon is not explicitly set in data
  if (!data.heroIcon) {
    data.heroIcon = `/odoo/icons/apps/${data.icon}.svg`;
  }

  // Fallback for relatedApps if not explicitly set for this app
  if (!data.relatedApps) {
    data.relatedApps = [
      { name: "CRM", icon: "crm", desc: "Manage leads", link: "/odoo/app/crm" },
      { name: "Project", icon: "project", desc: "Track tasks", link: "/odoo/app/project" },
      { name: "Accounting", icon: "accountant", desc: "Manage finances", link: "/app/accounting" }
    ];
  }

  return (
    <div
      className={`min-h-screen text-white overflow-hidden ${inter.className}`}
      style={{ '--font-main': inter.style.fontFamily }}
    >
      <div className="relative z-10">
        {/* 1. CUSTOM CENTERED APP HERO BANNER WITH THE EXACT FONT STYLING */}
        <AppHero data={data} />

        {/* 2. TESTIMONIAL SECTION (If available in app details) */}
        {data.testimonial && <IndustryTestimonial data={data} />}

        {/* 3. DYNAMIC SECTIONS MANAGER */}
        {data.sections?.map((section, idx) => {
          switch (section.type) {
            case "split-content":
              return <AppSplitContent key={idx} section={section} />;
            case "service-management":
              return <IndustryServiceManagement key={idx} section={section} />;
            case "video-stats":
              return <AppVideoStats key={idx} section={section} appName={appNameClean} />;
            case "secure-docs":
              return <IndustrySecureDocs key={idx} section={section} />;
            default:
              return null;
          }
        })}

        {/* 4. TRANSITION CALLOUT */}
        <OdooMoreCallout position="top" />

        {/* 5. FEATURES GRID */}
        {data.featuresGrid && <IndustryFeaturesGrid data={data} />}

        {/* 6. RELATED APPS */}
        <IndustryAppGrid data={data} />

        {/* 7. USERS MOSAIC */}
        <IndustryUsersMosaic />

        {/* 8. CTA SECTION */}
        <IndustryCTA data={data} />
      </div>
    </div>
  );
}
